import { Component, ElementRef, EventEmitter, Output, ViewChild } from '@angular/core';
import { filter, fromEvent, merge, Subscription, switchMap, takeUntil, tap, timer } from 'rxjs';

@Component({
  selector: 'holdable-div',
  templateUrl: './holdable-div.component.html',
  styleUrls: ['./holdable-div.component.scss']
})
export class HoldableDivComponent {
 @ViewChild('holdDiv') holdDiv!: ElementRef<HTMLDivElement>;
  @Output() holdEvent = new EventEmitter<void>();

  private holdSubscription: Subscription | undefined;
  holding: boolean = false;
  countdown: number = 5000; // 5 seconds in milliseconds
  private readonly HOLD_DURATION = 5000; // 5 seconds

  constructor() {}

  ngAfterViewInit(): void {
    const mouseDown$ = fromEvent(this.holdDiv.nativeElement, 'mousedown');
    const mouseUp$ = fromEvent(this.holdDiv.nativeElement, 'mouseup');
    const mouseLeave$ = fromEvent(this.holdDiv.nativeElement, 'mouseleave');

    const touchStart$ = fromEvent(this.holdDiv.nativeElement, 'touchstart');
    const touchEnd$ = fromEvent(this.holdDiv.nativeElement, 'touchend');
    const touchCancel$ = fromEvent(this.holdDiv.nativeElement, 'touchcancel');

    // Combine all start events
    const startEvents$ = merge(
      mouseDown$.pipe(filter((event: Event) => (event as MouseEvent).button === 0)), // Only left mouse click
      touchStart$.pipe(tap((event: Event) => event.preventDefault())) // Prevent default touch behavior (e.g., scrolling)
    );

    // Combine all end/cancel events
    const endEvents$ = merge(
      mouseUp$,
      mouseLeave$,
      touchEnd$,
      touchCancel$
    );

    this.holdSubscription = startEvents$.pipe(
      tap(() => {
        this.holding = true;
        this.countdown = this.HOLD_DURATION; // Reset countdown on new hold
      }),
      switchMap(() => {
        return timer(0, 100) // Emit every 100ms for countdown
          .pipe(
            tap(() => {
              this.countdown -= 100;
              if (this.countdown <= 0) {
                this.countdown = 0; // Cap at 0 to avoid negative numbers
              }
            }),
            takeUntil(endEvents$),
            filter(val => val * 100 >= this.HOLD_DURATION - 100) // Trigger just before the duration
          );
      })
    ).subscribe(() => {
      // Ensure we are still holding and the countdown has reached zero (or less due to timing)
      if (this.holding && this.countdown <= 0) {
        this.holdEvent.emit();
        this.holding = false; // Reset holding state after event
        this.countdown = this.HOLD_DURATION; // Reset countdown
      }
    });

    // Reset holding state if any end/cancel event occurs before the timer finishes
    endEvents$.subscribe(() => {
      if (this.holding) { // Only reset if currently holding
        this.holding = false;
        this.countdown = this.HOLD_DURATION;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.holdSubscription) {
      this.holdSubscription.unsubscribe();
    }
  }

  // Event handlers (optional, primarily for type safety and if you need separate logic)
  onMouseDown(event: MouseEvent): void {
    // RxJS stream handles the core logic
  }

  onMouseUp(event: MouseEvent): void {
    // RxJS stream handles the core logic
  }

  onMouseLeave(event: MouseEvent): void {
    // RxJS stream handles the core logic
  }

  onTouchStart(event: TouchEvent): void {
    // event.preventDefault() is handled in the RxJS pipe
    // This is important to prevent default browser behaviors like scrolling or zooming
  }

  onTouchEnd(event: TouchEvent): void {
    // RxJS stream handles the core logic
  }

  onTouchCancel(event: TouchEvent): void {
    // RxJS stream handles the core logic
  }


}
